import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Send, Calendar, Facebook, Youtube, MessageCircle, FolderOpen } from 'lucide-react';
import { SIDEBAR_MENU } from '../data';
import AdBlock from './AdBlock';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useSiteInfo } from '../context/SiteContext';

export default function Sidebar() {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const location = useLocation();
  const isHome = location.pathname === '/';
  
  const { siteInfo, categories: sidebarCategories, latestPosts } = useSiteInfo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      // Lưu trực tiếp vào Firestore collection 'contacts'
      await addDoc(collection(db, 'contacts'), {
        name: formData.name,
        phone: formData.phone,
        service: 'Báo giá nhanh (Sidebar)',
        message: formData.message,
        status: 'new',
        createdAt: new Date().toISOString()
      });
      
      setStatus('success');
      setFormData({ name: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending quote from sidebar:', error);
      setStatus('error');
    }
  };

  // latestPosts được lấy từ Firestore ở trên

  return (
    <aside className="w-full space-y-8">
      {/* Services List */}
      <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm">
        <h3 className="bg-primary text-white font-bold py-3 px-4 uppercase text-lg">
          Danh mục dịch vụ
        </h3>
        <ul className="divide-y divide-gray-100">
          {SIDEBAR_MENU.map((item, index) => {
            const isActive = location.pathname === item.link;
            return (
              <li key={`menu-${index}`}>
                <Link
                  to={item.link}
                  className={`flex items-center px-4 py-3 transition-colors group ${
                    isActive ? 'bg-blue-50/80 text-primary' : 'hover:bg-gray-50 hover:text-primary'
                  }`}
                >
                  <ChevronRight
                    size={16}
                    className={`mr-2 flex-shrink-0 transition-transform ${
                      isActive ? 'text-primary translate-x-0.5' : 'text-gray-400 group-hover:text-primary'
                    }`}
                  />
                  <span className={`font-medium ${isActive ? 'text-primary font-semibold' : 'text-gray-700 group-hover:text-primary'}`}>
                    {item.title}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Banner 1 - trên form báo giá */}
      <AdBlock position="sidebar_bottom" />

      {/* Sidebar Inline Quote Form */}
      <div className="bg-primary rounded-2xl overflow-hidden shadow-md">
        <h3 className="font-bold py-3 px-4 text-white uppercase text-base flex items-center">
          <Send size={16} className="mr-2" /> NHẬN BÁO GIÁ NHANH
        </h3>
        <div className="bg-blue-50 p-4">
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              placeholder="Họ và tên *"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            <input
              type="tel"
              placeholder="Số điện thoại *"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            <textarea
              placeholder="Nội dung yêu cầu..."
              rows={3}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-accent hover:bg-orange-300 text-primary-dark font-bold py-2.5 px-4 rounded-md transition-colors flex justify-center items-center cursor-pointer disabled:opacity-70 text-sm"
            >
              {status === 'loading' ? 'Đang gửi...' : <><Send size={16} className="mr-2" /> GỬI YÊU CẦU</>}
            </button>
            {status === 'success' && <p className="text-green-700 text-xs text-center bg-green-50 border border-green-200 p-2 rounded font-medium">Đã gửi thành công!</p>}
            {status === 'error' && <p className="text-red-600 text-xs text-center bg-red-50 border border-red-200 p-2 rounded font-medium">Lỗi, vui lòng thử lại.</p>}
          </form>
        </div>
      </div>

      {/* Latest News */}
      <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm">
        <h3 className="bg-primary text-white font-bold py-3 px-4 uppercase text-lg">Tin tức mới nhất</h3>
        <div className="p-4 space-y-4">
          {latestPosts.slice(0, 5).map((item) => (
            <div key={item.id} className="flex gap-3 group">
              <Link to={`/tin-tuc/${item.slug}`} className="w-28 aspect-video self-start flex-shrink-0 overflow-hidden rounded">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { e.currentTarget.src = 'https://placehold.co/100x100/eeeeee/999999?text=News'; }}
                />
              </Link>
              <div className="flex-1">
                <h4 className="font-medium text-sm line-clamp-2 leading-snug mb-1">
                  <Link to={`/tin-tuc/${item.slug}`} className="hover:text-primary transition-colors text-gray-800 uppercase">{item.title}</Link>
                </h4>
                <div className="flex items-center text-xs text-gray-500">
                  <Calendar size={12} className="mr-1" />
                  <span>{item.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hotline Support Widget */}
      <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm p-4 text-center border-t-4 border-t-primary">
        <h3 className="font-bold text-primary uppercase mb-2">Hỗ Trợ Trực Tuyến</h3>
        <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEgwFkEfnpMvANtFGAK3-vD_0DL78nxGKfq3pt-BMXJ1gt88pV57Kmrh-XK7lydZw7H6JuyH6sjac3TMpqVAsrm63BiEdfujuNCXdOkDTAv9QlFjT0Okn9DSHM41fFZgYVonvSSziDCQrQUjPNzQ1x3S314DVvKxds1zlKu7RZ8oJ2Mh2EL1jNpX7L0MQhn6/s1600/png-clipart-technical-support-computer-icons-service-support-miscellaneous-blue-thumbnail.png" className="w-20 h-20 rounded-full mx-auto mb-3 border-2 border-primary p-1 bg-white" alt="Support" loading="lazy" decoding="async" />
        <p className="text-sm text-gray-600 mb-1">Tư vấn kỹ thuật & Báo giá</p>
        <div className="font-black text-xl text-primary mb-2">0968.387.568</div>
        <a href="tel:0968387568" className="inline-block bg-accent text-primary-dark font-bold px-4 py-2 rounded-full text-sm hover:bg-orange-300 transition-colors">
          Gọi ngay
        </a>
      </div>

      {/* Banner 2 - Dành riêng sidebar trang chủ (nếu trang trống) */}
      <AdBlock position="home_sidebar" />


      {/* Social Networks */}
      <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm">
        <h3 className="bg-primary text-white font-bold py-3 px-4 uppercase text-lg">Theo dõi chúng tôi</h3>
        <div className="p-4 grid grid-cols-3 gap-3">
          <a href="https://facebook.com/khoancocnhoicom" target="_blank" rel="noopener noreferrer" className="bg-[#1877F2] hover:bg-[#166fe5] text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors">
            <Facebook size={24} className="mb-1" />
            <span className="text-[10px] uppercase font-bold text-blue-50">Facebook</span>
          </a>
          <a href="https://zalo.me/0904033100" target="_blank" rel="noopener noreferrer" className="bg-[#0068FF] hover:bg-[#005ce6] text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors">
            <MessageCircle size={24} className="mb-1" />
            <span className="text-[10px] uppercase font-bold text-blue-50">Zalo OA</span>
          </a>
          <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="bg-[#FF0000] hover:bg-[#e60000] text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors">
            <Youtube size={24} className="mb-1" />
            <span className="text-[10px] uppercase font-bold text-blue-50">YouTube</span>
          </a>
        </div>
      </div>

      {/* Popular Posts */}
      <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm">
        <h3 className="bg-primary text-white font-bold py-3 px-4 uppercase text-lg">Bài viết quan tâm</h3>
        <ul className="divide-y divide-gray-100 p-2">
          {latestPosts.slice(1, 6).map((item, index) => (
            <li key={`pop-${item.id}`}>
              <Link to={`/tin-tuc/${item.slug}`} className="flex items-start p-2 gap-3 hover:bg-gray-50 transition-colors group rounded">
                <div className="w-28 aspect-video self-start flex-shrink-0 overflow-hidden rounded">
                  <img
                    src={item.image || 'https://dummyimage.com/100x100/eeeeee/999999.png&text=News'}
                    alt={item.title}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.currentTarget.src = 'https://dummyimage.com/100x100/eeeeee/999999.png&text=News'; }}
                  />
                </div>
                <span className="font-medium text-sm text-gray-700 group-hover:text-primary leading-snug uppercase line-clamp-3">{item.title}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      {/* Banner dưới bài viết quan tâm */}
      <AdBlock position="home_sidebar" />

      {/* Chuyên mục */}
      {sidebarCategories.filter(c => !c.parentId).length > 0 && (
        <div className="bg-white border border-slate-200/70 rounded-2xl overflow-hidden shadow-sm">
          <h3 className="bg-primary text-white font-bold py-3 px-4 uppercase text-lg flex items-center gap-2">
            <FolderOpen size={18} /> Chuyên mục
          </h3>
          <ul className="divide-y divide-gray-100">
            {sidebarCategories.filter(c => !c.parentId).map(parent => {
              const children = sidebarCategories.filter(c => c.parentId === parent.id);
              return (
                <li key={parent.id}>
                  <Link
                    to={`/tin-tuc?category=${parent.slug}`}
                    className="flex items-center px-4 py-3 hover:bg-gray-50 hover:text-primary transition-colors group"
                  >
                    <ChevronRight size={15} className="text-gray-400 group-hover:text-primary mr-2 flex-shrink-0" />
                    <span className="font-medium text-gray-700 group-hover:text-primary text-sm">{parent.name}</span>
                  </Link>
                  {children.map(child => (
                    <Link
                      key={child.id}
                      to={`/tin-tuc?category=${child.slug}`}
                      className="flex items-center pl-9 pr-4 py-2.5 hover:bg-gray-50 hover:text-primary transition-colors border-t border-gray-50 group"
                    >
                      <span className="text-gray-400 mr-2 text-xs">—</span>
                      <span className="text-gray-600 group-hover:text-primary text-sm">{child.name}</span>
                    </Link>
                  ))}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Banner Cuối Sidebar */}
      <AdBlock position="home_sidebar_bottom" />

    </aside>
  );
}
